import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "@/i18n/LocaleLink";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterForm = ({ dark = false }) => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — hidden from humans
  const [sending, setSending] = useState(false);
  const { ui } = useContent();
  const { lang } = useLang();
  const n = ui.newsletter;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email, lang, website });
      toast.success(data.message);
      setEmail("");
    } catch {
      toast.error(n.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-md" data-testid="newsletter-form">
      <div className={`flex items-center border-b ${dark ? "border-cream/30" : "border-ink/30"} focus-within:border-clay transition-colors duration-300`}>
        <label htmlFor={dark ? "newsletter-email-dark" : "newsletter-email"} className="sr-only">{n.placeholder}</label>
        <input
          id={dark ? "newsletter-email-dark" : "newsletter-email"}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={n.placeholder}
          aria-label={n.placeholder}
          data-testid="newsletter-email-input"
          className={`flex-1 bg-transparent py-3 text-base outline-none placeholder:text-soft/60 ${dark ? "text-cream placeholder:text-cream/50" : "text-ink"}`}
        />
        <button
          type="submit"
          disabled={sending}
          data-testid="newsletter-submit-button"
          className="group flex items-center gap-2 py-3 pl-4 text-sm tracking-wide text-clay disabled:opacity-50"
        >
          {sending ? n.sending : n.subscribe}
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="newsletter-honeypot" />
      </div>
      <p className={`mt-3 text-xs leading-relaxed ${dark ? "text-cream/50" : "text-soft/70"}`} data-testid="newsletter-privacy-note">
        {n.note}{" "}
        <Link to="/privacy" className={`underline underline-offset-2 ${dark ? "hover:text-cream/80" : "hover:text-clay"} transition-colors duration-300`} data-testid="newsletter-privacy-link">
          {n.privacyLink}
        </Link>
      </p>
    </form>
  );
};
